import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { matchFocusAreas, type FocusArea } from "@/lib/focusAreas";

/**
 * Surfaces whether the signed-in user already has a readiness assessment on file
 * and, if so, infers their focus areas from their company profile so we can
 * highlight "Coach recommended" opportunities across the site.
 */
export function useCoachReadiness() {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    enabled: !!user,
    queryKey: ["coach-readiness", user?.id],
    queryFn: async () => {
      const { data: profile } = await supabase
        .from("company_profiles")
        .select("id, industry, goals, funding_sought, county")
        .eq("user_id", user!.id)
        .maybeSingle();

      if (!profile) {
        return { hasProfile: false, hasAssessment: false, focusAreas: [] as FocusArea[], profile: null };
      }

      const { data: assessment } = await supabase
        .from("company_assessments")
        .select("id, created_at, summary, gaps, recommended_actions, funding_pathways")
        .eq("company_profile_id", profile.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      // Flatten any nested JSON values into a single searchable string so we
      // can pull focus-area signal out of the assessment as well as the profile.
      const flatten = (val: unknown): string => {
        if (!val) return "";
        if (typeof val === "string") return val;
        if (typeof val === "number" || typeof val === "boolean") return String(val);
        if (Array.isArray(val)) return val.map(flatten).join(" ");
        if (typeof val === "object") return Object.values(val as Record<string, unknown>).map(flatten).join(" ");
        return "";
      };

      const haystack = [
        profile.industry,
        profile.goals,
        ...(profile.funding_sought ?? []),
        assessment?.summary,
        flatten(assessment?.gaps),
        flatten(assessment?.recommended_actions),
        flatten(assessment?.funding_pathways),
      ]
        .filter(Boolean)
        .join(" ");
      const focusAreas = matchFocusAreas(haystack);

      return {
        hasProfile: true,
        hasAssessment: !!assessment,
        focusAreas,
        profile,
      };
    },
    staleTime: 5 * 60 * 1000,
  });

  return {
    loading: isLoading,
    hasProfile: data?.hasProfile ?? false,
    hasAssessment: data?.hasAssessment ?? false,
    focusAreas: (data?.focusAreas ?? []) as FocusArea[],
    profile: data?.profile ?? null,
  };
}

/** Map GrantFinder's short area labels to canonical FocusArea strings. */
export const SHORT_AREA_TO_FOCUS: Record<string, FocusArea> = {
  Mobility: "Economic & Community Development",
  Climate: "Climate & Environment",
  Education: "Education & Youth",
  Justice: "Criminal Justice Reform",
  Health: "Health & Wellness",
};

export const FOCUS_TO_SHORT_AREA: Record<FocusArea, string> = {
  "Economic & Community Development": "Mobility",
  "Climate & Environment": "Climate",
  "Education & Youth": "Education",
  "Criminal Justice Reform": "Justice",
  "Health & Wellness": "Health",
};